import { useEffect, useState } from 'react';
import type { ChartPeriod, PeriodAvailability } from '@/api/types';
import { TOGGLE_STYLES, TOGGLE_BUTTON_BASE } from '@/styles/toggleStyles';

const VALID_PERIODS: ChartPeriod[] = ['3m', '6m', '1y', 'max', 'future'];

interface PeriodSelectorProps {
    /** Currently selected period */
    value: ChartPeriod;
    /** Callback when period changes */
    onChange: (period: ChartPeriod) => void;
    /** Period availability flags */
    availability: PeriodAvailability;
    /** Disable all options */
    disabled?: boolean;
}

interface PeriodOption {
    label: string;
    value: ChartPeriod;
    ariaLabel: string;
    getIsDisabled: (availability: PeriodAvailability) => boolean;
    getDisabledReason: (availability: PeriodAvailability) => string | undefined;
    /** Hide this option on mobile to save space */
    hiddenOnMobile?: boolean;
}

const options: PeriodOption[] = [
    {
        label: '3m',
        value: '3m',
        ariaLabel: '3 months',
        getIsDisabled: (a) => !a.can3m,
        getDisabledReason: () => 'Requires at least 3 months of data',
    },
    {
        label: '6m',
        value: '6m',
        ariaLabel: '6 months',
        getIsDisabled: (a) => !a.can6m,
        getDisabledReason: () => 'Requires at least 6 months of data',
        hiddenOnMobile: true,
    },
    {
        label: '1y',
        value: '1y',
        ariaLabel: '1 year',
        getIsDisabled: (a) => !a.can1y,
        getDisabledReason: () => 'Requires at least 1 year of data',
    },
    {
        label: 'Max',
        value: 'max',
        ariaLabel: 'Maximum range',
        getIsDisabled: () => false, // Max is always available
        getDisabledReason: () => undefined,
    },
    {
        label: 'Future',
        value: 'future',
        ariaLabel: 'Future projection',
        getIsDisabled: (a) => !a.canFuture,
        getDisabledReason: (a) =>
            a.futureUnavailableReason ||
            'Future projections require at least 2 years of historical data',
    },
];

/**
 * Period selector component for choosing chart time range.
 * Positioned at the bottom of the chart area.
 * Styled as a button group following the GranularityToggle pattern.
 *
 * Features:
 * - 5 period options: 3m, 6m, 1y, Max, Future
 * - Active button uses neutral gray styling
 * - Unavailable buttons stay clickable (aria-disabled) and show a visible
 *   hint explaining why — title tooltips are invisible on touch devices
 * - Accessibility: aria-pressed, aria-disabled, aria-label for each button
 */
export function PeriodSelector({ value, onChange, availability, disabled = false }: PeriodSelectorProps) {
    const [unavailableHint, setUnavailableHint] = useState<string | null>(null);

    // Auto-dismiss the hint after a few seconds
    useEffect(() => {
        if (unavailableHint === null) return;
        const timeout = setTimeout(() => setUnavailableHint(null), 5000);
        return () => clearTimeout(timeout);
    }, [unavailableHint]);

    return (
        <div className="flex flex-col items-center gap-1">
            <div className={`${TOGGLE_STYLES.container} ${disabled ? 'opacity-50' : ''}`}>
                {options.map((option) => {
                    const isActive = value === option.value;
                    const isOptionDisabled = disabled || option.getIsDisabled(availability);
                    const disabledReason = disabled
                        ? 'Not enough data'
                        : isOptionDisabled
                        ? option.getDisabledReason(availability)
                        : undefined;

                    return (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                                if (isOptionDisabled) {
                                    setUnavailableHint(disabledReason ?? 'Not enough data');
                                    return;
                                }
                                setUnavailableHint(null);
                                onChange(option.value);
                            }}
                            className={`
                                px-3 py-1.5 text-sm font-medium ${TOGGLE_BUTTON_BASE} relative
                                ${option.hiddenOnMobile ? 'hidden sm:inline-block' : ''}
                                ${
                                    isActive
                                        ? TOGGLE_STYLES.active
                                        : isOptionDisabled
                                          ? TOGGLE_STYLES.disabled
                                          : TOGGLE_STYLES.inactive
                                }
                                ${isOptionDisabled ? 'cursor-not-allowed' : ''}
                            `}
                            aria-pressed={isActive}
                            aria-disabled={isOptionDisabled}
                            aria-label={option.ariaLabel}
                            title={disabledReason}
                        >
                            {option.label}
                        </button>
                    );
                })}
            </div>
            {unavailableHint && (
                <p role="status" className="text-xs text-gray-500 dark:text-gray-400">
                    {unavailableHint}
                </p>
            )}
        </div>
    );
}

/**
 * Hook for managing period preference with localStorage persistence.
 * @param storageKey - The localStorage key (e.g., 'period-net-worth')
 * @param defaultValue - Default period if no stored preference (default: 'max')
 * @returns [currentPeriod, setPeriod] tuple
 */
export function usePeriodPreference(
    storageKey: string,
    defaultValue: ChartPeriod = 'max'
): [ChartPeriod, (period: ChartPeriod) => void] {
    const [period, setPeriodState] = useState<ChartPeriod>(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored && VALID_PERIODS.includes(stored as ChartPeriod)) {
                return stored as ChartPeriod;
            }
        } catch {
            // localStorage might be unavailable
        }
        return defaultValue;
    });

    const setPeriod = (newPeriod: ChartPeriod) => {
        setPeriodState(newPeriod);
        try {
            localStorage.setItem(storageKey, newPeriod);
        } catch {
            // localStorage might be unavailable
        }
    };

    // Sync with localStorage on mount if key changes
    useEffect(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored && VALID_PERIODS.includes(stored as ChartPeriod)) {
                setPeriodState(stored as ChartPeriod);
            }
        } catch {
            // localStorage might be unavailable
        }
    }, [storageKey]);

    return [period, setPeriod];
}
